import type { CSSProperties, ElementType } from 'react'

import cn from 'clsx'
import { Box } from '@/components/Box/Box'
import type { ResponsivePropRequireOne } from '@/utils/responsiveProps'

import type { Breakpoints } from '@/@types/styles'
import { Image } from '@/components/image/image'

import css from './avatar.module.css'

export type AvatarProps = {
    src: string
    alt?: string
    size?: ResponsivePropRequireOne<number>
    loading?: 'lazy' | 'eager'
    tag?: ElementType
    className?: string
}

function mapSizeToCSSProps(size: AvatarProps['size']) {
    if (typeof size === 'number') {
        return {
            customProperties: {
                '--avatar-prop-size': `${size}px`,
            },
            dimensions: size,
        }
    }

    const mappedSize: { [key in Breakpoints as `--avatar-prop-size-${key}`]?: string } = {}

    if (size) {
        for (const [value, space] of Object.entries(size)) {
            mappedSize[`--avatar-prop-size-${value as Breakpoints}`] = `${space}px`
        }
    }

    return {
        customProperties: mappedSize,
        dimensions: size ? Object.entries(size)[0][1] : 36,
    }
}

export function Avatar({ size = 36, tag = 'div', className, ...rest }: AvatarProps) {
    const { customProperties, dimensions } = mapSizeToCSSProps(size)

    return (
        <Box
            tag={tag}
            className={cn(css.avatar, className)}
            style={customProperties as CSSProperties}
        >
            <Image width={dimensions} height={dimensions} {...rest} />
        </Box>
    )
}
